/**
 * Fusion de dossiers — le contrat d'échange entre l'API et les écrans
 * « Doublons probables », « Comparer deux fiches » et « Fusions récentes ».
 */
import { z } from 'zod';
import { TYPES_BENEFICIAIRE } from '../rules/beneficiaire.js';
const SEXES = ['M', 'F'];
/** Pourquoi deux fiches sont proposées comme doublon probable. */
export const RAISONS_DOUBLON = [
    /** Même matricule, même entreprise — deux contrats, ou le même. */
    'MEME_MATRICULE',
    /** Même date de naissance, noms proches — y compris dans deux entreprises. */
    'MEME_NAISSANCE_NOM_PROCHE',
    /** Noms très proches — même entreprise ou même titulaire dans la liste générale, partout depuis une fiche. */
    'NOM_TRES_PROCHE',
];
export const LIBELLE_RAISON_DOUBLON = {
    MEME_MATRICULE: 'Même matricule dans la même entreprise',
    MEME_NAISSANCE_NOM_PROCHE: 'Même date de naissance, noms proches',
    NOM_TRES_PROCHE: 'Noms très proches',
};
/** Une fiche, telle qu'on la lit pour la comparer à une autre. Rien de médical. */
export const schemaFicheAComparer = z.object({
    id: z.uuid(),
    typeBeneficiaire: z.enum(TYPES_BENEFICIAIRE),
    nomComplet: z.string(),
    matricule: z.string().nullable(),
    dateNaissance: z.string().nullable(),
    sexe: z.enum(SEXES).nullable(),
    fonction: z.string().nullable(),
    lieuAffectation: z.string().nullable(),
    dateEntree: z.string().nullable(),
    statut: z.string(),
    etatConfirmation: z.string(),
    origine: z.string(),
    adherent: z.object({ id: z.uuid(), code: z.string(), nom: z.string() }).nullable(),
    offre: z.string().nullable(),
    /** Pour un ayant droit : le salarié auquel il se rattache. */
    titulaire: z.object({ id: z.uuid(), nomComplet: z.string() }).nullable(),
    nbVisites: z.number().int(),
    derniereVisiteLe: z.string().nullable(),
    nbAyantsDroit: z.number().int(),
    creeLe: z.string(),
});
export const schemaDoublonProbable = z.object({
    a: schemaFicheAComparer,
    b: schemaFicheAComparer,
    raison: z.enum(RAISONS_DOUBLON),
    /** Ressemblance des noms, de 0 à 1. */
    ressemblance: z.number(),
});
export const schemaFiltresDoublons = z
    .object({
    /** Les doublons probables d'UNE fiche — depuis sa fiche patient — plutôt que la liste générale. */
    personneId: z.uuid().optional(),
    /** Restreint la liste générale : un nom, une raison sociale ou un code d'entreprise. */
    recherche: z.string().trim().min(2).max(100).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(50),
})
    .strict();
export const schemaListeDoublons = z.object({ doublons: z.array(schemaDoublonProbable) });
export const schemaFiltreComparaison = z.object({ a: z.uuid(), b: z.uuid() }).strict();
export const DIVERGENCES_FUSION = ['DATE_NAISSANCE', 'SEXE'];
export const OBSTACLES_FUSION = [
    'MEME_FICHE',
    'LIEN_DE_PARENTE',
    'AYANTS_DROIT_SANS_TITULAIRE',
    'ECART_OFFRE_OUVERT',
];
export const schemaComparaisonFiches = z.object({
    a: schemaFicheAComparer,
    b: schemaFicheAComparer,
    divergences: z.array(z.enum(DIVERGENCES_FUSION)),
    /** La fiche que la plateforme recommande de conserver : celle qui porte l'histoire. */
    conserverRecommandee: z.uuid(),
    /** Ce qui empêche la fusion si l'on conserve `a`, puis si l'on conserve `b` ; `null` = possible. */
    obstacles: z.object({
        enConservantA: z.enum(OBSTACLES_FUSION).nullable(),
        enConservantB: z.enum(OBSTACLES_FUSION).nullable(),
    }),
    /** Cette paire a déjà été examinée et déclarée « deux personnes différentes ». */
    dejaEcartee: z.boolean(),
});
export const schemaDemandeFusion = z
    .object({
    conserveeId: z.uuid(),
    absorbeeId: z.uuid(),
    /** Ce qui prouve que c'est la même personne : lisible à l'audit, jamais vide. */
    motif: z.string().trim().min(5).max(1000),
    /** À cocher quand la date de naissance ou le sexe diffèrent : on fusionne en le sachant. */
    divergencesAcceptees: z.boolean().default(false),
})
    .strict();
export const schemaPaireEcartee = z.object({ a: z.uuid(), b: z.uuid() }).strict();
const personneResumee = z.object({ id: z.uuid(), nomComplet: z.string() });
export const schemaFusion = z.object({
    id: z.uuid(),
    conservee: personneResumee,
    absorbee: personneResumee,
    motif: z.string(),
    /** Ce qui a été reporté, en nombres : le détail reste en base, pour défaire. */
    reporte: z.object({
        visites: z.number().int(),
        ayantsDroit: z.number().int(),
        champsCompletes: z.array(z.string()),
    }),
    fusionneeLe: z.string(),
    fusionneePar: z.string().nullable(),
    annuleeLe: z.string().nullable(),
    motifAnnulation: z.string().nullable(),
});
export const schemaListeFusions = z.object({ fusions: z.array(schemaFusion) });
export const schemaAnnulationFusion = z
    .object({ motif: z.string().trim().min(5).max(1000) })
    .strict();
//# sourceMappingURL=fusion.js.map